import { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Paper,
} from "@mui/material";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";

const LOGS_USER_TABLE_COLUMN_COUNT = 5;

const formatActivityDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime()) || date.getFullYear() <= 1970) {
    return "N/A";
  }
  return date.toLocaleString();
};

const LogsUserTable = ({
  userList,
  isLoading,
  userFetchError,
  onRetry,
  onRowClick,
  totalRows,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = (columnKey) => {
    if (sortColumn !== columnKey) {
      setSortColumn(columnKey);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortColumn(null);
      setSortDirection("asc");
    }
  };

  const sortedRows = useMemo(() => {
    if (!userList || !userList.length || !sortColumn) return userList || [];
    return [...userList].sort((a, b) => {
      let av = a[sortColumn];
      let bv = b[sortColumn];
      if (sortColumn === "lastActivityLoggedDate") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (typeof av === "string" || typeof bv === "string") {
        const cmp = String(av || "").localeCompare(String(bv || ""), undefined, { numeric: true, sensitivity: "base" });
        return sortDirection === "asc" ? cmp : -cmp;
      }
      return sortDirection === "asc" ? (av ?? 0) - (bv ?? 0) : (bv ?? 0) - (av ?? 0);
    });
  }, [userList, sortColumn, sortDirection]);

  return (
    <div className="logs_table_container">
      <TableContainer component={Paper}>
        <Table aria-label="users table">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: "10%" }}>SL NO</TableCell>
              <TableCell style={{ width: "25%" }}>
                <TableSortLabel
                  active={sortColumn === "userName"}
                  direction={sortColumn === "userName" ? sortDirection : "asc"}
                  onClick={() => handleSort("userName")}
                >
                  User Name
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "15%" }}>
                <TableSortLabel
                  active={sortColumn === "patientId"}
                  direction={sortColumn === "patientId" ? sortDirection : "asc"}
                  onClick={() => handleSort("patientId")}
                >
                  Patient ID
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "15%" }}>
                <TableSortLabel
                  active={sortColumn === "userId"}
                  direction={sortColumn === "userId" ? sortDirection : "asc"}
                  onClick={() => handleSort("userId")}
                >
                  User ID
                </TableSortLabel>
              </TableCell>
              <TableCell style={{ width: "35%" }}>
                <TableSortLabel
                  active={sortColumn === "lastActivityLoggedDate"}
                  direction={sortColumn === "lastActivityLoggedDate" ? sortDirection : "asc"}
                  onClick={() => handleSort("lastActivityLoggedDate")}
                >
                  Last Activity Date
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows && sortedRows.length > 0 ? (
              sortedRows.map((user, index) => (
                <TableRow
                  key={user.patientId || user.userId || index}
                  onClick={() => onRowClick(user)}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#f8fafc",
                    },
                  }}
                >
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>
                    <span className="patient-name-link">{user.userName || "-"}</span>
                  </TableCell>
                  <TableCell>{user.patientId || "-"}</TableCell>
                  <TableCell>{user.userId || "-"}</TableCell>
                  <TableCell>{formatActivityDate(user.lastActivityLoggedDate)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableStatusRow
                colSpan={LOGS_USER_TABLE_COLUMN_COUNT}
                loading={isLoading}
                error={userFetchError}
                onRetry={onRetry}
                emptyMessage="No data available"
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="logs_pagination">
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </div>
    </div>
  );
};

export default LogsUserTable;
